/*
 * BioIndex
 * Copyright (c) 2026 BioIndex™. All rights reserved.
 * Unauthorized copying, scraping, redistribution, or commercial reuse is prohibited.
 */

import { Link, useLocation } from "react-router-dom";

const LEVELS = [
  { key: "kingdom", label: "Kingdom" },
  { key: "phylum", label: "Phylum" },
  { key: "class", label: "Class" },
  { key: "order", label: "Order" },
  { key: "family", label: "Family" },
  { key: "genus", label: "Genus" },
];

function titleCase(str) {
  if (!str) return "—";
  return str
    .toLowerCase()
    .split(" ")
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ");
}

export default function TaxonomyStepper({
  params,
  currentLevel,
  uid,
  browseMode = "taxonomy",
  setBrowseMode,
}) {
  const {
    kingdomName,
    phylumName,
    className,
    orderName,
    familyName,
    genusName,
  } = params;

  const values = {
    kingdom: kingdomName,
    phylum: phylumName,
    class: className,
    order: orderName,
    family: familyName,
    genus: genusName,
  };

  const location = useLocation();
  const exploreMatch = location.pathname.match(/^\/explore\/([^/]+)/);
  const exploreUID = exploreMatch ? exploreMatch[1] : null;
  const effectiveUID = uid || exploreUID;

  const adminModeActive =
    !!effectiveUID &&
    new URLSearchParams(location.search).get("adminMode") === "true";

  const rawPaths = {
    kingdom: effectiveUID ? `/explore/${effectiveUID}` : "/explore",

    phylum:
      kingdomName &&
      (effectiveUID
        ? `/explore/${effectiveUID}/kingdom/${kingdomName}`
        : `/kingdom/${kingdomName}`),

    class:
      kingdomName &&
      phylumName &&
      (effectiveUID
        ? `/explore/${effectiveUID}/kingdom/${kingdomName}/phylum/${phylumName}`
        : `/kingdom/${kingdomName}/phylum/${phylumName}`),

    order:
      kingdomName &&
      phylumName &&
      className &&
      (effectiveUID
        ? `/explore/${effectiveUID}/kingdom/${kingdomName}/phylum/${phylumName}/class/${className}`
        : `/kingdom/${kingdomName}/phylum/${phylumName}/class/${className}`),

    family:
      kingdomName &&
      phylumName &&
      className &&
      orderName &&
      (effectiveUID
        ? `/explore/${effectiveUID}/kingdom/${kingdomName}/phylum/${phylumName}/class/${className}/order/${orderName}`
        : `/kingdom/${kingdomName}/phylum/${phylumName}/class/${className}/order/${orderName}`),

    genus:
      kingdomName &&
      phylumName &&
      className &&
      orderName &&
      familyName &&
      (effectiveUID
        ? `/explore/${effectiveUID}/kingdom/${kingdomName}/phylum/${phylumName}/class/${className}/order/${orderName}/family/${familyName}`
        : `/kingdom/${kingdomName}/phylum/${phylumName}/class/${className}/order/${orderName}/family/${familyName}`),
  };

  const paths = Object.fromEntries(
    Object.entries(rawPaths).map(([level, path]) => {
      if (!path || !adminModeActive) {
        return [level, path];
      }

      const separator = path.includes("?") ? "&" : "?";

      return [level, `${path}${separator}adminMode=true`];
    }),
  );

  return (
    <div className="w-full">
      <div className="mx-auto w-full max-w-6xl sm:px-2 sm:pt-2">
        <div className="w-full overflow-hidden border-b border-[#93CA87]/40 bg-white shadow-sm sm:mx-auto sm:w-fit sm:max-w-full sm:rounded-2xl sm:border">
          <div className="overflow-x-auto overscroll-x-contain [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
            <div className="flex min-w-max snap-x snap-mandatory items-start justify-start gap-2 px-3 pb-1.5 pt-3 sm:justify-center sm:gap-7 sm:px-6 sm:py-3">
              {LEVELS.map((level, i) => {
                const isCompleted =
                  level.key === "genus"
                    ? Boolean(familyName)
                    : Boolean(values[level.key]);

                const isCurrent = currentLevel === level.key;
                const isTaxonomyLink = Boolean(paths[level.key]) && !isCurrent;

                const isModeResetButton =
                  Boolean(paths[level.key]) &&
                  isCurrent &&
                  browseMode !== "taxonomy" &&
                  typeof setBrowseMode === "function";

                let bubbleStyle =
                  "bg-white text-[#374049]/45 border border-[#93CA87]/30";

                if (isCompleted) {
                  bubbleStyle =
                    "bg-[#374049] text-white border border-[#93CA87]/50 shadow-sm";
                }

                if (isCurrent) {
                  bubbleStyle =
                    "bg-[#24C3BE] text-white border border-[#24C3BE] ring-4 ring-[#24C3BE]/20 shadow-sm";
                }

                const Bubble = (
                  <div
                    className={`w-8 h-8 sm:w-9 sm:h-9 rounded-full flex items-center justify-center text-xs sm:text-sm font-semibold transition ${bubbleStyle}`}
                  >
                    {i + 1}
                  </div>
                );

                return (
                  <div
                    key={level.key}
                    className="flex min-w-[72px] snap-center flex-col items-center sm:min-w-[90px]"
                  >
                    {isTaxonomyLink ? (
                      <Link
                        to={paths[level.key]}
                        onClick={() => {
                          if (typeof setBrowseMode === "function") {
                            setBrowseMode("taxonomy");
                          }
                        }}
                        className="hover:scale-105 transition"
                      >
                        {Bubble}
                      </Link>
                    ) : isModeResetButton ? (
                      <button
                        type="button"
                        onClick={() => setBrowseMode("taxonomy")}
                        className="hover:scale-105 transition"
                      >
                        {Bubble}
                      </button>
                    ) : (
                      Bubble
                    )}

                    <div className="mt-1 text-[11px] sm:text-xs text-[#24C3BE] font-medium">
                      {level.label}
                    </div>

                    <div
                      title={titleCase(values[level.key])}
                      className={`mt-1 max-w-[84px] truncate text-center text-xs sm:max-w-none sm:text-sm ${
                        isCompleted || isCurrent
                          ? "text-[#374049]"
                          : "text-[#374049]/40"
                      }`}
                      style={{ fontFamily: "Cormorant Garamond" }}
                    >
                      {titleCase(values[level.key])}
                    </div>
                  </div>
                );
              })}

              {typeof setBrowseMode === "function" && (
                <div className="hidden sm:contents">
                  <div className="mx-1 h-16 w-px self-center bg-[#93CA87]/30" />

                  <button
                    type="button"
                    onClick={() =>
                      setBrowseMode(
                        browseMode === "alpha" ? "taxonomy" : "alpha",
                      )
                    }
                    className="flex flex-col items-center min-w-[58px] sm:min-w-[74px] group"
                    title="Browse all photos alphabetically"
                  >
                    <div
                      className={`w-8 h-8 sm:w-9 sm:h-9 rounded-full flex items-center justify-center text-sm transition ${
                        browseMode === "alpha"
                          ? "bg-[#24C3BE] text-white border border-[#24C3BE] ring-4 ring-[#24C3BE]/20 shadow-sm"
                          : "bg-white text-[#374049]/60 border border-[#93CA87]/30 group-hover:bg-[#24C3BE]/10 group-hover:text-[#24C3BE]"
                      }`}
                    >
                      🔠
                    </div>

                    <div className="mt-1 text-[11px] sm:text-xs text-[#24C3BE] font-medium">
                      Alphabet
                    </div>

                    <div
                      className={`mt-1 text-xs sm:text-sm ${
                        browseMode === "alpha"
                          ? "text-[#374049]"
                          : "text-[#374049]/40"
                      }`}
                      style={{ fontFamily: "Cormorant Garamond" }}
                    ></div>
                  </button>

                  <button
                    type="button"
                    onClick={() =>
                      setBrowseMode(browseMode === "date" ? "taxonomy" : "date")
                    }
                    className="flex flex-col items-center min-w-[58px] sm:min-w-[74px] group"
                    title="Browse all photos by date"
                  >
                    <div
                      className={`w-8 h-8 sm:w-9 sm:h-9 rounded-full flex items-center justify-center text-sm transition ${
                        browseMode === "date"
                          ? "bg-[#24C3BE] text-white border border-[#24C3BE] ring-4 ring-[#24C3BE]/20 shadow-sm"
                          : "bg-white text-[#374049]/60 border border-[#93CA87]/30 group-hover:bg-[#24C3BE]/10 group-hover:text-[#24C3BE]"
                      }`}
                    >
                      📅
                    </div>

                    <div className="mt-1 text-[11px] sm:text-xs text-[#24C3BE] font-medium">
                      Date
                    </div>

                    <div
                      className={`mt-1 text-xs sm:text-sm ${
                        browseMode === "date"
                          ? "text-[#374049]"
                          : "text-[#374049]/40"
                      }`}
                      style={{ fontFamily: "Cormorant Garamond" }}
                    ></div>
                  </button>

                  <button
                    type="button"
                    onClick={() =>
                      setBrowseMode(
                        browseMode === "location" ? "taxonomy" : "location",
                      )
                    }
                    className="flex flex-col items-center min-w-[66px] sm:min-w-[90px] group"
                    title="Browse all photos by location"
                  >
                    <div
                      className={`w-8 h-8 sm:w-9 sm:h-9 rounded-full flex items-center justify-center text-sm transition ${
                        browseMode === "location"
                          ? "bg-[#24C3BE] text-white border border-[#24C3BE] ring-4 ring-[#24C3BE]/20 shadow-sm"
                          : "bg-white text-[#374049]/60 border border-[#93CA87]/30 group-hover:bg-[#24C3BE]/10 group-hover:text-[#24C3BE]"
                      }`}
                    >
                      🌐
                    </div>

                    <div className="mt-1 text-[11px] sm:text-xs text-[#24C3BE] font-medium">
                      Location
                    </div>

                    <div
                      className={`mt-1 text-xs sm:text-sm ${
                        browseMode === "location"
                          ? "text-[#374049]"
                          : "text-[#374049]/40"
                      }`}
                      style={{ fontFamily: "Cormorant Garamond" }}
                    ></div>
                  </button>
                </div>
              )}
            </div>
          </div>

          {typeof setBrowseMode === "function" && (
            <div className="grid grid-cols-3 border-t border-[#93CA87]/30 sm:hidden">
              <button
                type="button"
                onClick={() =>
                  setBrowseMode(browseMode === "alpha" ? "taxonomy" : "alpha")
                }
                className={`flex min-w-0 items-center justify-center gap-2 border-r border-[#93CA87]/30 px-2 py-2.5 text-xs font-semibold transition ${
                  browseMode === "alpha"
                    ? "bg-[#24C3BE]/15 text-[#374049]"
                    : "text-[#374049]/65 hover:bg-[#24C3BE]/10"
                }`}
                title="Browse all photos alphabetically"
              >
                <span
                  className={`flex h-7 w-7 items-center justify-center rounded-full border text-sm ${
                    browseMode === "alpha"
                      ? "border-[#24C3BE] bg-[#24C3BE]"
                      : "border-[#93CA87]/40 bg-white"
                  }`}
                  aria-hidden="true"
                >
                  🔠
                </span>

                <span>Alphabet</span>
              </button>

              <button
                type="button"
                onClick={() =>
                  setBrowseMode(browseMode === "date" ? "taxonomy" : "date")
                }
                className={`flex min-w-0 items-center justify-center gap-2 border-r border-[#93CA87]/30 px-2 py-2.5 text-xs font-semibold transition ${
                  browseMode === "date"
                    ? "bg-[#24C3BE]/15 text-[#374049]"
                    : "text-[#374049]/65 hover:bg-[#24C3BE]/10"
                }`}
                title="Browse all photos by date"
              >
                <span
                  className={`flex h-7 w-7 items-center justify-center rounded-full border text-sm ${
                    browseMode === "date"
                      ? "border-[#24C3BE] bg-[#24C3BE] text-white"
                      : "border-[#93CA87]/40 bg-white"
                  }`}
                  aria-hidden="true"
                >
                  📅
                </span>

                <span>Date</span>
              </button>

              <button
                type="button"
                onClick={() =>
                  setBrowseMode(
                    browseMode === "location" ? "taxonomy" : "location",
                  )
                }
                className={`flex min-w-0 items-center justify-center gap-2 px-2 py-2.5 text-xs font-semibold transition ${
                  browseMode === "location"
                    ? "bg-[#24C3BE]/15 text-[#374049]"
                    : "text-[#374049]/65 hover:bg-[#24C3BE]/10"
                }`}
                title="Browse all photos by location"
              >
                <span
                  className={`flex h-7 w-7 items-center justify-center rounded-full border text-sm ${
                    browseMode === "location"
                      ? "border-[#24C3BE] bg-[#24C3BE]"
                      : "border-[#93CA87]/40 bg-white"
                  }`}
                  aria-hidden="true"
                >
                  🌐
                </span>

                <span>Location</span>
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
